import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { AssetsResultProps, useEditor } from '@grapesjs/react';
import { mdiClose, mdiUpload, mdiLink, mdiImage, mdiLoading, mdiFolderStar, mdiFileDocument, mdiRefresh, mdiAlertCircle, mdiDelete, mdiCloseCircle } from '@mdi/js';
import Icon from '@mdi/react';
import type { Asset } from 'grapesjs';
import { newServer } from 'constants/constants';
import imageCompression from 'browser-image-compression';

// Constantes de validación
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024; // 10MB en bytes
const ALLOWED_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
const ALLOWED_EXTENSIONS = ['PNG', 'JPG', 'JPEG', 'GIF', 'WEBP'];

// Opciones de compresión de imagen
const COMPRESSION_OPTIONS = {
  maxSizeMB: 1, // Tamaño máximo después de compresión: 1MB
  maxWidthOrHeight: 1920, // Máximo 1920px de ancho o alto
  useWebWorker: true, // Usar Web Worker para no bloquear la UI
  fileType: 'image/webp', // Convertir a WebP para mejor compresión
  initialQuality: 0.8, // Calidad inicial 80%
};

// Función para comprimir imagen
const compressImage = async (file: File): Promise<{ compressedFile: File; originalSize: number; compressedSize: number }> => {
  const originalSize = file.size;
  
  try {
    // Si el archivo ya es pequeño (menor a 500KB), solo convertir a WebP sin comprimir mucho
    const options = file.size < 500 * 1024 
      ? { ...COMPRESSION_OPTIONS, maxSizeMB: 0.5, initialQuality: 0.9 }
      : COMPRESSION_OPTIONS;
    
    const compressedFile = await imageCompression(file, options);
    
    // Cambiar extensión del nombre a .webp
    const newFileName = file.name.replace(/\.[^.]+$/, '.webp');
    const finalFile = new File([compressedFile], newFileName, { type: 'image/webp' });
    
    return {
      compressedFile: finalFile,
      originalSize,
      compressedSize: finalFile.size,
    };
  } catch (error) {
    console.error('Error compressing image:', error);
    // Si falla la compresión, devolver el archivo original
    return {
      compressedFile: file,
      originalSize,
      compressedSize: file.size,
    };
  }
};

// Tipo para errores de validación
interface ValidationError {
  fileName: string;
  error: string;
  type: 'size' | 'format';
}

// Función para formatear tamaño de archivo
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Tipo para las imágenes de S3
interface S3Image {
  key: string;
  name: string;
  url: string;
  size: number;
  lastModified: string;
  folder: string;
  isShared: boolean;
}

// Función para subir imagen a S3 via backend
const uploadImageToS3 = async (
  base64Image: string,
  filename: string,
  pageId: string
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const response = await fetch(`${newServer}landing-page/upload-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pageId,
        image: base64Image,
        filename,
      }),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      return { success: true, url: data.url };
    } else {
      return { success: false, error: data.error || 'Upload failed' };
    }
  } catch (error: any) {
    console.error('Error uploading image to S3:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

// Función para obtener imágenes de S3
const fetchS3Images = async (pageId: string): Promise<S3Image[]> => {
  try {
    const response = await fetch(`${newServer}landing-page/images?pageId=${pageId}`);
    const data = await response.json();
    if (data.success) {
      return data.images || [];
    }
    return [];
  } catch (error) {
    console.error('Error fetching S3 images:', error);
    return [];
  }
};

// Función para eliminar imagen de S3
const deleteS3Image = async (key: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const url = `${newServer}landing-page/images?key=${encodeURIComponent(key)}`;
    console.log('[Delete] Calling:', url);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
      },
    });
    
    console.log('[Delete] Response status:', response.status);
    console.log('[Delete] Response headers:', response.headers.get('content-type'));
    
    const text = await response.text();
    console.log('[Delete] Response body:', text);
    
    // Try to parse as JSON
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('[Delete] JSON parse error, raw text:', text);
      return { success: false, error: `Invalid response: ${text.substring(0, 100)}` };
    }
    
    if (response.ok && data.success) {
      return { success: true };
    }
    return { success: false, error: data.error || 'Delete failed' };
  } catch (error: any) {
    console.error('[Delete] Error:', error);
    return { success: false, error: error.message || 'Network error' };
  }
};

interface CustomAssetManagerProps extends AssetsResultProps {
  currentPageId?: string;
}

export default function CustomAssetManager(props: CustomAssetManagerProps) {
  const { assets, select, open, Container, currentPageId } = props;
  const editor = useEditor();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [uploadTarget, setUploadTarget] = useState<'shared' | 'page'>('page');
  const [s3Images, setS3Images] = useState<S3Image[]>([]);
  const [loadingS3, setLoadingS3] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; image: S3Image | null; deleting: boolean }>({
    show: false,
    image: null,
    deleting: false,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Cargar imágenes de S3 cuando se abre el modal
  useEffect(() => {
    if (open && currentPageId) {
      loadS3Images();
    }
  }, [open, currentPageId]);

  const loadS3Images = async () => {
    if (!currentPageId) return;
    setLoadingS3(true);
    const images = await fetchS3Images(currentPageId);
    setS3Images(images);
    setLoadingS3(false);
  };

  // Si no está abierto, no renderizar nada
  if (!open) {
    return null;
  }

  const remove = (asset: Asset) => {
    editor.Assets.remove(asset);
  };

  const handleAddUrl = () => {
    if (urlInput.trim()) {
      editor.Assets.add({
        src: urlInput.trim(),
        name: urlInput.split('/').pop() || 'image',
      });
      setUrlInput('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddUrl();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;
    processFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Función para validar un archivo
  const validateFile = (file: File): ValidationError | null => {
    // Validar formato
    if (!ALLOWED_FORMATS.includes(file.type)) {
      const extension = file.name.split('.').pop()?.toUpperCase() || 'desconocido';
      return {
        fileName: file.name,
        error: `Formato no permitido (.${extension}). Solo se permiten: ${ALLOWED_EXTENSIONS.join(', ')}`,
        type: 'format'
      };
    }
    
    // Validar tamaño
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        fileName: file.name,
        error: `El archivo pesa ${formatFileSize(file.size)}. El tamaño máximo permitido es ${MAX_FILE_SIZE_MB}MB`,
        type: 'size'
      };
    }
    
    return null;
  };

  // Limpiar errores de validación
  const clearValidationErrors = () => {
    setValidationErrors([]);
  };

  const processFiles = async (files: FileList) => {
    // Limpiar errores anteriores
    setValidationErrors([]);
    
    // Si es "shared" usamos "shared", si es "page" usamos el currentPageId
    const targetId = uploadTarget === 'shared' ? 'shared' : (currentPageId || 'page-unknown');
    
    const fileArray = Array.from(files);
    const errors: ValidationError[] = [];
    const validFiles: File[] = [];
    
    // Validar todos los archivos primero
    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        validFiles.push(file);
      }
    }
    
    // Si hay errores, mostrarlos
    if (errors.length > 0) {
      setValidationErrors(errors);
    }
    
    // Si no hay archivos válidos, salir
    if (validFiles.length === 0) {
      return;
    }
    
    setUploading(true);
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      
      try {
        // PASO 1: Comprimir imagen
        setUploadProgress(`Comprimiendo ${i + 1}/${validFiles.length}: ${file.name}...`);
        const { compressedFile, originalSize, compressedSize } = await compressImage(file);
        
        const savedPercent = Math.round((1 - compressedSize / originalSize) * 100);
        console.log(`[Compression] ${file.name}: ${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (${savedPercent}% reducido)`);
        
        // PASO 2: Subir imagen comprimida
        setUploadProgress(`Subiendo ${i + 1}/${validFiles.length}: ${compressedFile.name} (${formatFileSize(compressedSize)}) - Ahorraste ${savedPercent}%`);
        
        // Leer archivo comprimido como base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result === 'string') {
              resolve(result);
            } else {
              reject(new Error('Failed to read file'));
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(compressedFile);
        });

        // Subir a S3 (imagen comprimida en formato WebP)
        const result = await uploadImageToS3(base64, compressedFile.name, targetId);

        if (result.success && result.url) {
          // Agregar asset con URL de S3
          editor.Assets.add({
            src: result.url,
            name: compressedFile.name,
          });
          console.log(`[S3 Upload] Success (${targetId}): ${result.url} | Original: ${formatFileSize(originalSize)} → Compressed: ${formatFileSize(compressedSize)}`);
        } else {
          console.error(`[S3 Upload] Failed for ${compressedFile.name}:`, result.error);
          // Agregar error de upload a la lista de errores
          setValidationErrors(prev => [...prev, {
            fileName: file.name,
            error: `Error al subir: ${result.error || 'Error desconocido'}`,
            type: 'format' // Usamos format como tipo genérico para errores de upload
          }]);
        }
      } catch (error) {
        console.error(`Error processing ${file.name}:`, error);
        setValidationErrors(prev => [...prev, {
          fileName: file.name,
          error: `Error al procesar: ${(error as Error).message || 'Error desconocido'}`,
          type: 'format'
        }]);
      }
    }

    setUploading(false);
    setUploadProgress('');
    // Recargar imágenes de S3 después de subir
    loadS3Images();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) {
      processFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  // Seleccionar una imagen de S3 existente
  const handleS3ImageSelect = (image: S3Image) => {
    // Agregar a los assets del editor y seleccionar
    const asset = editor.Assets.add({
      src: image.url,
      name: image.name,
    });
    if (asset) {
      select(asset as Asset, true);
    }
  };

  // Abrir modal de confirmación para eliminar
  const handleS3ImageDelete = (image: S3Image, e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que se seleccione la imagen
    setDeleteModal({ show: true, image, deleting: false });
  };

  // Confirmar eliminación de imagen
  const confirmDeleteImage = async () => {
    if (!deleteModal.image) return;
    
    setDeleteModal(prev => ({ ...prev, deleting: true }));
    
    const result = await deleteS3Image(deleteModal.image.key);
    if (result.success) {
      loadS3Images();
      setDeleteModal({ show: false, image: null, deleting: false });
    } else {
      alert(`Error eliminando imagen: ${result.error}`);
      setDeleteModal(prev => ({ ...prev, deleting: false }));
    }
  };

  // Cerrar modal de eliminación
  const closeDeleteModal = () => {
    if (!deleteModal.deleting) {
      setDeleteModal({ show: false, image: null, deleting: false });
    }
  };

  // Separar imágenes compartidas de las de la página
  const sharedImages = s3Images.filter(img => img.isShared);
  const pageImages = s3Images.filter(img => !img.isShared);

  const content = (
    <div className="custom-asset-manager">
      {/* URL Input Section */}
      <div className="cam-section">
        <label className="cam-label">
          <Icon path={mdiLink} size={0.7} />
          Add from URL
        </label>
        <div className="cam-url-row">
          <input
            type="text"
            className="cam-url-input"
            placeholder="https://example.com/image.jpg"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={uploading}
          />
          <button 
            className="cam-add-btn"
            onClick={handleAddUrl}
            disabled={!urlInput.trim() || uploading}
          >
            Add
          </button>
        </div>
      </div>

      {/* Upload Section */}
      <div className="cam-section">
        <label className="cam-label">
          <Icon path={mdiUpload} size={0.7} />
          Upload to AWS S3
        </label>
        
        {/* Target selector */}
        <div className="cam-upload-target">
          <button
            className={`cam-target-btn ${uploadTarget === 'shared' ? 'cam-target-active' : ''}`}
            onClick={() => setUploadTarget('shared')}
            disabled={uploading}
          >
            <Icon path={mdiFolderStar} size={0.6} />
            Shared (all pages)
          </button>
          <button
            className={`cam-target-btn ${uploadTarget === 'page' ? 'cam-target-active' : ''}`}
            onClick={() => setUploadTarget('page')}
            disabled={uploading}
          >
            <Icon path={mdiFileDocument} size={0.6} />
            This page only ({currentPageId})
          </button>
        </div>

        <div 
          className={`cam-dropzone ${dragOver ? 'cam-dropzone-active' : ''} ${uploading ? 'cam-dropzone-disabled' : ''}`}
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {uploading ? (
            <>
              <Icon path={mdiLoading} size={1.5} className="cam-dropzone-icon cam-spin" />
              <p className="cam-dropzone-text">{uploadProgress}</p>
              <p className="cam-dropzone-hint">Uploading to S3...</p>
            </>
          ) : (
            <>
              <Icon path={mdiUpload} size={1.5} className="cam-dropzone-icon" />
              <p className="cam-dropzone-text">
                Arrastra imágenes aquí o <span>haz clic para seleccionar</span>
              </p>
              <p className="cam-dropzone-hint">
                {ALLOWED_EXTENSIONS.join(', ')} • Máximo {MAX_FILE_SIZE_MB}MB por imagen
              </p>
              <p className="cam-dropzone-hint" style={{ marginTop: '4px', opacity: 0.7 }}>
                Destino: {uploadTarget === 'shared' ? 'Carpeta compartida' : currentPageId}
              </p>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          multiple
          onChange={handleFileChange}
          disabled={uploading}
        />
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="cam-section cam-validation-errors">
          <div className="cam-errors-header">
            <Icon path={mdiAlertCircle} size={0.8} className="cam-error-icon" />
            <span>Errores de validación ({validationErrors.length})</span>
            <button 
              className="cam-errors-close" 
              onClick={clearValidationErrors}
              title="Cerrar errores"
            >
              <Icon path={mdiCloseCircle} size={0.7} />
            </button>
          </div>
          <div className="cam-errors-list">
            {validationErrors.map((error, index) => (
              <div key={index} className={`cam-error-item cam-error-${error.type}`}>
                <div className="cam-error-filename">
                  <Icon 
                    path={error.type === 'size' ? mdiImage : mdiAlertCircle} 
                    size={0.6} 
                  />
                  <span>{error.fileName}</span>
                </div>
                <p className="cam-error-message">{error.error}</p>
              </div>
            ))}
          </div>
          <p className="cam-errors-hint">
            <strong>Requisitos:</strong> Formatos: {ALLOWED_EXTENSIONS.join(', ')} • Tamaño máximo: {MAX_FILE_SIZE_MB}MB
          </p>
        </div>
      )}

      {/* Shared S3 Images */}
      {sharedImages.length > 0 && (
        <div className="cam-section">
          <label className="cam-label cam-label-shared">
            <Icon path={mdiFolderStar} size={0.7} />
            Shared images ({sharedImages.length})
            <button className="cam-refresh-btn" onClick={loadS3Images} disabled={loadingS3}>
              <Icon path={mdiRefresh} size={0.6} className={loadingS3 ? 'cam-spin' : ''} />
            </button>
          </label>
          <div className="cam-grid">
            {sharedImages.map((image) => (
              <div
                key={image.key}
                className="cam-asset cam-asset-s3 cam-asset-shared"
                onClick={() => handleS3ImageSelect(image)}
              >
                <img 
                  className="cam-asset-img" 
                  src={image.url} 
                  alt={image.name} 
                />
                <div className="cam-asset-overlay">
                  <span className="cam-asset-select">Use</span>
                </div>
                <button
                  className="cam-asset-remove"
                  onClick={(e) => handleS3ImageDelete(image, e)}
                  title="Delete image"
                >
                  <Icon size={0.6} path={mdiClose} />
                </button>
                <span className="cam-asset-badge cam-badge-shared">Shared</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Page S3 Images */}
      {pageImages.length > 0 && (
        <div className="cam-section">
          <label className="cam-label cam-label-page">
            <Icon path={mdiFileDocument} size={0.7} />
            {currentPageId} images ({pageImages.length})
            {sharedImages.length === 0 && (
              <button className="cam-refresh-btn" onClick={loadS3Images} disabled={loadingS3}>
                <Icon path={mdiRefresh} size={0.6} className={loadingS3 ? 'cam-spin' : ''} />
              </button>
            )}
          </label>
          <div className="cam-grid">
            {pageImages.map((image) => (
              <div
                key={image.key}
                className="cam-asset cam-asset-s3"
                onClick={() => handleS3ImageSelect(image)}
              >
                <img 
                  className="cam-asset-img" 
                  src={image.url} 
                  alt={image.name} 
                />
                <div className="cam-asset-overlay">
                  <span className="cam-asset-select">Use</span>
                </div>
                <button
                  className="cam-asset-remove"
                  onClick={(e) => handleS3ImageDelete(image, e)}
                  title="Delete image"
                >
                  <Icon size={0.6} path={mdiClose} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading S3 */}
      {loadingS3 && s3Images.length === 0 && (
        <div className="cam-section cam-loading">
          <Icon path={mdiLoading} size={1.2} className="cam-spin" />
          <p>Loading S3 images...</p>
        </div>
      )}

      {/* Session uploaded assets */}
      {assets.length > 0 && (
        <div className="cam-section">
          <label className="cam-label">
            <Icon path={mdiImage} size={0.7} />
            Uploaded this session ({assets.length})
          </label>
          <div className="cam-grid">
            {assets.map((asset) => (
              <div
                key={asset.getSrc()}
                className="cam-asset"
                onClick={() => select(asset, true)}
              >
                <img 
                  className="cam-asset-img" 
                  src={asset.getSrc()} 
                  alt={asset.get('name') || ''} 
                />
                <div className="cam-asset-overlay">
                  <span className="cam-asset-select">Select</span>
                </div>
                <button
                  className="cam-asset-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(asset);
                  }}
                >
                  <Icon size={0.6} path={mdiClose} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Container>
      {content}
      
      {/* Delete confirmation modal */}
      {deleteModal.show && deleteModal.image && (
        <div className="cam-delete-modal-overlay" onClick={closeDeleteModal}>
          <div className="cam-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cam-delete-modal-header">
              <Icon path={mdiAlertCircle} size={1.2} className="cam-delete-icon" />
              <h3>Delete Image</h3>
            </div>
            
            <div className="cam-delete-modal-body">
              <div className="cam-delete-preview">
                <img src={deleteModal.image.url} alt={deleteModal.image.name} />
              </div>
              <p className="cam-delete-filename">{deleteModal.image.name}</p>
              
              {deleteModal.image.isShared && (
                <div className="cam-delete-warning">
                  <Icon path={mdiAlertCircle} size={0.7} />
                  <span>This image is <strong>SHARED</strong> and may be in use on other pages.</span>
                </div>
              )}
              
              <p className="cam-delete-confirm-text">
                Are you sure you want to delete this image? This action cannot be undone.
              </p>
            </div>
            
            <div className="cam-delete-modal-footer">
              <button 
                className="cam-delete-btn-cancel" 
                onClick={closeDeleteModal}
                disabled={deleteModal.deleting}
              >
                Cancel
              </button>
              <button 
                className="cam-delete-btn-confirm" 
                onClick={confirmDeleteImage}
                disabled={deleteModal.deleting}
              >
                {deleteModal.deleting ? (
                  <>
                    <Icon path={mdiLoading} size={0.7} className="cam-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Icon path={mdiDelete} size={0.7} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
